import { useEffect, useRef, useState } from 'react';
import { request } from '../services/api';

const initialQueueState = {
  loading: false,
  joining: false,
  result: null,
  message: 'Lobby ready.',
  waitEndsAt: null,
  playerCount: 1,
  queuedModeTitle: null,
};

export function useMatchmaking(profile, onWalletSync) {
  const pollRef = useRef(null);
  const joinInFlightRef = useRef(false);
  const lastQueuedModeRef = useRef(null);
  const [queueState, setQueueState] = useState({ ...initialQueueState, activeMatch: null });

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    stopPolling();
    joinInFlightRef.current = false;
    lastQueuedModeRef.current = null;
    setQueueState({ ...initialQueueState, activeMatch: null });
  }, [profile?.mobile]);

  function stopPolling() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  function resetQueueState(message = 'Lobby ready.') {
    stopPolling();
    joinInFlightRef.current = false;
    lastQueuedModeRef.current = null;
    setQueueState({ ...initialQueueState, activeMatch: null, message });
  }

  function syncWallet(result, message) {
    if (onWalletSync && (typeof result?.balance === 'number' || Array.isArray(result?.transactions))) {
      onWalletSync(result, message);
    }
  }

  function startQueuePolling(mode) {
    if (!profile?.mobile) return;
    const modeTitle = mode?.title || 'Quick Match';
    lastQueuedModeRef.current = modeTitle;
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const result = await request(`/api/matches/queue-status?mobile=${encodeURIComponent(profile.mobile)}&mode=${encodeURIComponent(modeTitle)}`);
        if (result.status === 'matched' && result.match) {
          stopPolling();
          joinInFlightRef.current = false;
          lastQueuedModeRef.current = null;
          setQueueState({
            loading: false,
            joining: false,
            result: result.match,
            waitEndsAt: null,
            playerCount: result.match?.players?.length || 4,
            queuedModeTitle: modeTitle,
            message: 'Table ready. Opening board...',
          });
          return;
        }
        if (result.status === 'queued') {
          setQueueState((prev) => ({
            ...prev,
            loading: true,
            joining: false,
            queuedModeTitle: modeTitle,
            playerCount: result.playerCount || prev.playerCount || 1,
            waitEndsAt: result.waitEndsAt || prev.waitEndsAt,
            message: result.message || 'Finding players...',
          }));
          return;
        }
        if (result.status === 'idle') {
          resetQueueState('Lobby ready.');
        }
      } catch (error) {
        setQueueState((prev) => ({
          ...prev,
          joining: false,
          loading: Boolean(prev.loading),
          message: error.message || prev.message,
        }));
      }
    }, 1500);
  }

  async function queueForMatch(mode) {
    const modeTitle = mode?.title || 'Quick Match';
    if (!profile?.mobile) {
      setQueueState({
        ...initialQueueState,
        message: 'Login required before matchmaking.',
        queuedModeTitle: modeTitle,
      });
      return null;
    }

    if (joinInFlightRef.current) return null;
    if (queueState.loading && queueState.queuedModeTitle === modeTitle) return null;
    if (queueState.result?.roomId) return queueState.result;

    joinInFlightRef.current = true;
    setQueueState({
      loading: true,
      joining: true,
      result: null,
      waitEndsAt: null,
      playerCount: 1,
      queuedModeTitle: modeTitle,
      message: 'Joining table...',
    });

    try {
      const result = await request('/api/matches/join', {
        method: 'POST',
        body: JSON.stringify({ mobile: profile.mobile, mode }),
      });
      syncWallet(result, result.reused ? 'Queue already active. Wallet kept in sync.' : 'Queue joined. Coins will deduct when the match actually starts.');
      if (result.status === 'queued') {
        joinInFlightRef.current = false;
        setQueueState({
          loading: true,
          joining: false,
          result: null,
          waitEndsAt: result.waitEndsAt || null,
          playerCount: result.playerCount || 1,
          queuedModeTitle: modeTitle,
          message: result.message || 'Finding players...',
        });
        startQueuePolling(mode);
        return result;
      }
      stopPolling();
      joinInFlightRef.current = false;
      lastQueuedModeRef.current = null;
      setQueueState({
        loading: false,
        joining: false,
        result,
        waitEndsAt: null,
        playerCount: result?.players?.length || 4,
        queuedModeTitle: modeTitle,
        message: 'Table ready. Opening board...',
      });
      return result;
    } catch (error) {
      stopPolling();
      joinInFlightRef.current = false;
      const activeMatch = error?.payload?.activeMatch || null;
      setQueueState({
        loading: false,
        joining: false,
        result: null,
        waitEndsAt: null,
        playerCount: 1,
        queuedModeTitle: modeTitle,
        message: error.message,
        activeMatch,
      });
      return { ok: false, error: error.message, activeMatch, code: error?.payload?.code || null };
    }
  }

  async function cancelQueue(mode) {
    const modeTitle = mode?.title || lastQueuedModeRef.current || queueState.queuedModeTitle || 'Quick Match';
    if (!profile?.mobile) {
      resetQueueState('Lobby ready.');
      return null;
    }
    try {
      const result = await request('/api/matches/cancel', {
        method: 'POST',
        body: JSON.stringify({ mobile: profile.mobile, mode: mode || { title: modeTitle } }),
      });
      resetQueueState('Queue cancelled. No coins deducted.');
      syncWallet(result, 'Queue cancelled. Wallet synced.');
      return result;
    } catch (error) {
      resetQueueState(error.message || 'Queue cleared.');
      return null;
    }
  }

  return {
    queueState,
    queueForMatch,
    cancelQueue,
    resetQueueState,
    isJoining: queueState.joining,
  };
}
